import { SDK_PROTOCOL_VERSION } from '@riz/contracts/sdk';
import type { SdkEnvironment } from './environment.ts';

export { SDK_PROTOCOL_VERSION };

/** Every message the SDK posts to, or accepts from, the Riz host wrapper. */
export const HOST_MESSAGES = {
  hostReady: 'riz:host-ready',
  session: 'riz:session',
  error: 'riz:error',
  result: 'riz:result',
  signal: 'riz:signal',
} as const;

export const CLIENT_MESSAGES = {
  hello: 'riz:hello',
  requestSession: 'riz:request-session',
  operation: 'riz:op',
  beacon: 'riz:beacon',
  embedProgress: 'riz:embed-progress',
  embedReady: 'riz:embed-ready',
  embedError: 'riz:embed-error',
} as const;

export type OperationKind = 'event' | 'query' | 'command';

export interface Operation {
  kind: OperationKind;
  /** `event` names come from the shared event list; `query`/`command` are routes. */
  name: string;
  payload: Record<string, unknown>;
  /** Present for queued gameplay events; absent for queries and commands. */
  eventId?: string;
  seq?: number;
  timeoutMs?: number;
}

export interface HandshakeRequest {
  gameKey: string;
  sdkVersion: string;
  protocol: number;
  frameOrigin: string;
}

export interface HandshakeResponse {
  protocol: number;
  environment: SdkEnvironment;
  runId: string;
  nonce: string;
  player: SdkPlayer | null;
  game: SdkGame | null;
  limits: Record<string, number>;
  capabilities: string[];
  /** Set while a major version is inside its final support phase. */
  deprecation?: { message: string; sunsetAt: string } | null;
}

export interface SdkPlayer {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  rpBalance: number;
  gamesPlayed: number;
  isGuest: boolean;
  personalBest?: number;
}

export interface SdkGame {
  id: string;
  slug: string;
  title: string;
  verified: boolean;
  rewardsEnabled: boolean;
  sitelock?: boolean;
}

/**
 * Null when the host speaks a protocol this build does not. Unknown fields are
 * ignored rather than rejected, so the host may add to the session payload
 * without breaking a game built against an earlier shape.
 */
export function resolveHandshake(raw: Record<string, unknown>): HandshakeResponse | null {
  const protocol = typeof raw.protocol === 'number' ? raw.protocol : SDK_PROTOCOL_VERSION;
  if (protocol !== SDK_PROTOCOL_VERSION) return null;

  return {
    protocol,
    environment: (raw.environment as SdkEnvironment) ?? 'production',
    runId: typeof raw.runId === 'string' ? raw.runId : '',
    nonce: typeof raw.nonce === 'string' ? raw.nonce : '',
    player: (raw.player as SdkPlayer) ?? null,
    game: (raw.game as SdkGame) ?? null,
    limits: (raw.limits as Record<string, number>) ?? {},
    capabilities: Array.isArray(raw.capabilities)
      ? raw.capabilities.filter((value): value is string => typeof value === 'string')
      : [],
    deprecation: (raw.deprecation as HandshakeResponse['deprecation']) ?? null,
  };
}
